import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Edit3, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useMutation } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import * as Sentry from '@sentry/react';
import { toast } from "sonner";

interface ExperimentDetailsEditorModalProps {
  experiment: {
    id: string;
    name: string;
    description?: string;
    status?: 'active' | 'completed' | 'draft';
    introductionUrl?: string;
    qrUrl?: string;
    image?: string;
    landingPageUrl?: string;
    shopUrl?: string;
    brandProductUrl?: string;
    price?: number;
  };
  onSave?: () => void;
}

const ExperimentDetailsEditorModal: React.FC<ExperimentDetailsEditorModalProps> = ({
  experiment,
  onSave
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: experiment.name || "",
    description: experiment.description || "",
    status: experiment.status || "draft",
    introduction_url: experiment.introductionUrl || "",
    qr_url: experiment.qrUrl || "",
    image_url: experiment.image || "",
    landing_page_url: experiment.landingPageUrl || "",
    shop_url: experiment.shopUrl || "",
    brand_product_url: experiment.brandProductUrl || "",
    price_usd: experiment.price || 0,
  });

  const saveMutation = useMutation({
    mutationFn: async (updatedData: typeof formData) => {
      // Transform status to uppercase and map 'completed' to 'CLOSED'
      const transformedData = {
        ...updatedData,
        status: updatedData.status === 'completed' ? 'CLOSED' : updatedData.status.toUpperCase()
      };

      const { data: apiResponse, error } = await supabase.functions.invoke('tastermonial-api', {
        body: {
          method: 'PUT',
          endpoint: `/api/v2/experiments/${experiment.id}`,
          body: transformedData
        },
      });

      if (error) {
        throw new Error(error.message || 'Failed to save experiment details');
      }

      if (apiResponse.status !== 200) {
        throw new Error(`API error: ${apiResponse.statusText || 'Failed to save experiment details'}`);
      }

      return apiResponse.data;
    },
    onSuccess: () => {
      toast.success("Experiment details saved successfully");
      setIsOpen(false);
      onSave?.(); // Refresh the experiment query
    },
    onError: (error) => {
      toast.error(`Failed to save experiment details: ${error.message}`);
              Sentry.captureException(error);
    },
  });

  const handleInputChange = (field: keyof typeof formData, value: string | number) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSave = () => {
    saveMutation.mutate(formData);
  };

  const resetForm = () => {
    setFormData({
      name: experiment.name || "",
      description: experiment.description || "",
      status: experiment.status || "draft",
      introduction_url: experiment.introductionUrl || "",
      qr_url: experiment.qrUrl || "",
      image_url: experiment.image || "",
      landing_page_url: experiment.landingPageUrl || "",
      shop_url: experiment.shopUrl || "",
      brand_product_url: experiment.brandProductUrl || "",
      price_usd: experiment.price || 0,
    });
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      resetForm(); // Reset form when closing modal
    }
    setIsOpen(open);
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="h-6 w-6 p-0 ml-2 opacity-60 hover:opacity-100"
        >
          <Edit3 className="h-3 w-3" />
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden">
        <DialogHeader>
          <DialogTitle>Edit Experiment Details</DialogTitle>
          <p className="text-xs text-muted-foreground mt-1">
            Update the experiment details and click Save to apply changes.
          </p>
        </DialogHeader>
        <div className="overflow-auto" style={{ maxHeight: '60vh' }}>
          <div className="space-y-4 p-1">
            <div className="space-y-2">
              <Label htmlFor="name">Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => handleInputChange("name", e.target.value)}
                placeholder="Experiment name"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => handleInputChange("description", e.target.value)}
                placeholder="Short description of the experiment"
                rows={3}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="status">Status</Label>
              <Select
                value={formData.status}
                onValueChange={(value) => handleInputChange("status", value as 'active' | 'completed' | 'draft')}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="price_usd">Price (USD)</Label>
              <Input
                id="price_usd"
                type="number"
                min="0"
                step="0.01"
                value={formData.price_usd}
                onChange={(e) => handleInputChange("price_usd", parseFloat(e.target.value) || 0)}
                placeholder="0.00"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="image_url">Image URL</Label>
              <Input
                id="image_url"
                value={formData.image_url}
                onChange={(e) => handleInputChange("image_url", e.target.value)}
                placeholder="https://example.com/image.jpg"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="landing_page_url">Landing Page URL</Label>
              <Input
                id="landing_page_url"
                value={formData.landing_page_url}
                onChange={(e) => handleInputChange("landing_page_url", e.target.value)}
                placeholder="https://example.com/landing"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="shop_url">Shop URL</Label>
              <Input
                id="shop_url"
                value={formData.shop_url}
                onChange={(e) => handleInputChange("shop_url", e.target.value)}
                placeholder="https://example.com/shop"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="brand_product_url">Brand Product URL</Label>
              <Input
                id="brand_product_url"
                value={formData.brand_product_url}
                onChange={(e) => handleInputChange("brand_product_url", e.target.value)}
                placeholder="https://example.com/product"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="introduction_url">Introduction URL</Label>
              <Input
                id="introduction_url"
                value={formData.introduction_url}
                onChange={(e) => handleInputChange("introduction_url", e.target.value)}
                placeholder="https://example.com/intro"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="qr_url">QR URL</Label>
              <Input
                id="qr_url"
                value={formData.qr_url}
                onChange={(e) => handleInputChange("qr_url", e.target.value)}
                placeholder="https://example.com/qr"
              />
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-4">
          <Button variant="outline" onClick={() => setIsOpen(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={saveMutation.isPending || !formData.name.trim()}
            className="min-w-[80px]"
          >
            {saveMutation.isPending ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              "Save"
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ExperimentDetailsEditorModal;